import React, { useState } from 'react';

export const GardenApartmentsFAQ: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs = [
    {
      question: 'What makes garden apartments different from other multifamily properties?',
      answer: 'Garden apartments are characterized by their low-rise construction (typically 2-3 stories), extensive landscaping and outdoor spaces, suburban locations, and horizontal spread across larger land parcels. Unlike high-rise or mid-rise buildings, garden apartments don\'t require elevators, have lower construction and operating costs, and create a more residential, neighborhood-like atmosphere. They typically feature surface parking rather than structured parking, and emphasize outdoor amenities and green spaces.'
    },
    {
      question: 'Do you buy garden apartments in any condition?',
      answer: 'Yes, we purchase garden apartments in any condition. Whether your property is fully renovated and stabilized, needs cosmetic updates, requires major capital improvements, or has significant deferred maintenance, we can make an offer. We have experience with properties needing roof replacements, parking lot repairs, building system upgrades, unit renovations, and landscaping improvements. Our team specializes in properties that other buyers might avoid due to condition issues.'
    },
    {
      question: 'What size garden apartment properties do you purchase?',
      answer: 'We buy garden apartments of all sizes, from small 20-unit communities to large 300+ unit complexes and portfolios. Our team has the capital and experience to handle transactions ranging from $1 million to $50 million+. Whether you own a single small property or a portfolio of garden apartment communities, we can structure a purchase that works for your situation.'
    },
    {
      question: 'How do you determine the value of a garden apartment property?',
      answer: 'We evaluate garden apartments using multiple approaches including income capitalization (based on net operating income and market cap rates), sales comparison (recent transactions of similar properties), and replacement cost analysis. We consider factors like location, property condition, tenant quality, lease structures, operating expenses, deferred maintenance, capital expenditure needs, and local market conditions. Our goal is to provide fair market value offers that reflect realistic assessments of the property\'s current state and income potential.'
    },
    {
      question: 'Can I sell my garden apartments with tenants in place?',
      answer: 'Absolutely. We prefer to purchase occupied properties and will assume all existing leases at closing. You don\'t need to terminate leases, vacate units, or deal with tenant relocations. We handle all tenant relationships after closing, including lease enforcement, maintenance requests, and renewals. Keeping tenants in place also means you continue receiving rental income right up until the closing date.'
    },
    {
      question: 'How long does it take to close on a garden apartment sale?',
      answer: 'We can typically close in 30-45 days from contract execution, though we\'re flexible on timing. If you need a faster closing, we can often accommodate 21-30 day closings. If you need more time to relocate, complete a 1031 exchange, or handle other matters, we can extend the closing date to 60-90 days or longer. Our goal is to work on your timeline while maintaining transaction momentum.'
    },
    {
      question: 'What documents do I need to provide for you to make an offer?',
      answer: 'Ideally, we\'d like to see rent rolls, trailing 12-month operating statements, recent property tax bills, insurance information, and any inspection reports or capital needs assessments you have. However, we understand that documentation may be incomplete, especially for smaller properties or those that have been owner-managed. We can work with whatever information you have available and conduct our own analysis to fill gaps. Don\'t let incomplete records prevent you from reaching out.'
    },
    {
      question: 'Do you charge any fees or commissions?',
      answer: 'No. We don\'t charge any fees, commissions, or costs to sellers. There are no upfront fees, evaluation fees, or hidden charges. You receive the full purchase price we agree upon, minus standard closing costs like title insurance and transfer taxes that apply to any property sale. By selling directly to us, you also avoid paying broker commissions that would typically be 3-6% of the sale price.'
    },
    {
      question: 'What if my garden apartments have environmental issues or code violations?',
      answer: 'We have experience dealing with environmental concerns and code compliance issues. Whether your property has asbestos, lead paint, underground storage tanks, soil contamination, or outstanding code violations, we can evaluate the situation and factor remediation costs into our offer. We work with environmental consultants and understand the regulatory landscape, so these issues don\'t automatically disqualify properties from purchase.'
    },
    {
      question: 'Can you help with 1031 exchange timing requirements?',
      answer: 'Yes, we regularly work with sellers completing 1031 exchanges and understand the strict timing requirements. We can structure closings to meet your 45-day identification and 180-day exchange deadlines. Our ability to close quickly and on specific dates makes us an ideal buyer for sellers with exchange timing pressures. We\'ll coordinate with your qualified intermediary to ensure all exchange requirements are met.'
    },
    {
      question: 'Do you offer seller financing options?',
      answer: 'Yes, we can structure seller financing arrangements if that interests you. Seller financing can provide tax advantages through installment sale treatment, generate ongoing income, and potentially achieve a higher sale price. We can discuss various structures including partial seller financing, wraparound mortgages, or land contracts. Our team will work with your tax advisors to create a structure that meets your financial and tax planning goals.'
    },
    {
      question: 'What happens to my property management staff after closing?',
      answer: 'That depends on your preference and our assessment of the property\'s needs. In some cases, we retain existing property management staff who know the property and tenants well. In other situations, we transition management to our own team or third-party management companies. If you have staff you\'d like us to consider retaining, we\'re happy to discuss that during our evaluation process.'
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Frequently Asked Questions
          </h2>
          <p className="text-xl text-gray-700 leading-relaxed">
            Common questions about selling garden apartments and our purchase process
          </p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-300"
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full px-8 py-6 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
              >
                <h3 className="text-lg font-bold text-gray-900 pr-8">
                  {faq.question}
                </h3>
                <div className={`flex-shrink-0 w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center transition-transform duration-300 ${openIndex === index ? 'rotate-180' : ''}`}>
                  <i className="ri-arrow-down-s-line text-white text-xl"></i>
                </div>
              </button>
              <div
                className={`overflow-hidden transition-all duration-300 ${
                  openIndex === index ? 'max-h-[1000px]' : 'max-h-0'
                }`}
              >
                <div className="px-8 pb-6">
                  <p className="text-gray-700 leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-16 text-center">
          <p className="text-lg text-gray-700 mb-6">
            Have more questions about selling your garden apartment property?
          </p>
          <a
            href="#get-offer"
            className="inline-block px-8 py-4 bg-orange-500 text-white font-bold rounded-lg hover:bg-orange-600 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap"
          >
            Contact Us Today
          </a>
        </div>
      </div>
    </section>
  );
};